import {
  DestroyRef,
  Injectable,
  NgZone,
  inject,
  signal,
} from '@angular/core';
import { DeviceService } from './device.service';

/**
 * Ringtone service using Web Audio API.
 * Plays a three-tone burst pattern for incoming calls.
 */
@Injectable({ providedIn: 'root' })
export class RingtoneService {
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly deviceService = inject(DeviceService);
  private readonly ringtoneSource = 'assets/audio/ringtone.mp3';

  private audioContext: AudioContext | null = null;
  private ringtoneAudio: HTMLAudioElement | null = null;
  private ringbackAudio: HTMLAudioElement | null = null;
  private ringbackDestination: MediaStreamAudioDestinationNode | null = null;
  private ringbackIntervalId: ReturnType<typeof setInterval> | null = null;
  private ringbackStopTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private ringbackGainNode: GainNode | null = null;
  private ringbackOscillators: OscillatorNode[] = [];

  readonly muted = signal(false);

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.stop();
      this.stopRingback();

      if (this.ringtoneAudio) {
        this.deviceService.setAuxiliaryAudioElement(null);
        this.ringtoneAudio.pause();
        this.ringtoneAudio.src = '';
        this.ringtoneAudio.load();
        this.ringtoneAudio = null;
      }

      if (this.ringbackAudio) {
        this.ringbackAudio.pause();
        this.ringbackAudio.srcObject = null;
        this.ringbackAudio.load();
        this.ringbackAudio = null;
      }
      this.ringbackDestination = null;

      if (this.audioContext) {
        void this.audioContext.close().catch(() => {
          // Best effort cleanup.
        });
        this.audioContext = null;
      }
    });
  }

  start(): void {
    if (this.muted()) {
      return;
    }

    // Incoming ringtone and ringback are mutually exclusive.
    this.stopRingback();
    this.ensureRingtoneElement();
    if (!this.ringtoneAudio || !this.ringtoneAudio.paused) {
      return;
    }

    this.deviceService.setAuxiliaryAudioElement(this.ringtoneAudio);
    this.ringtoneAudio.currentTime = 0;
    void this.ringtoneAudio.play().catch(() => {
      // Playback can be blocked by browser autoplay policy.
    });
  }

  stop(): void {
    if (!this.ringtoneAudio) {
      return;
    }

    this.ringtoneAudio.pause();
    this.ringtoneAudio.currentTime = 0;
    this.deviceService.setAuxiliaryAudioElement(null);
  }

  startRingback(): void {
    if (this.ringbackIntervalId) {
      return;
    }

    // Incoming ringtone and ringback are mutually exclusive.
    this.stop();
    this.ensureAudioContext();
    this.ensureRingbackOutputRoute();
    this.playRingbackCycle();

    this.zone.runOutsideAngular(() => {
      this.ringbackIntervalId = setInterval(() => {
        this.playRingbackCycle();
      }, 4000);
    });
  }

  stopRingback(): void {
    if (this.ringbackIntervalId) {
      clearInterval(this.ringbackIntervalId);
      this.ringbackIntervalId = null;
    }

    if (this.ringbackStopTimeoutId) {
      clearTimeout(this.ringbackStopTimeoutId);
      this.ringbackStopTimeoutId = null;
    }

    this.stopActiveRingbackTone();
    this.pauseRingbackOutputRoute();
  }

  toggleMute(isRinging: boolean): void {
    const wasMuted = this.muted();
    this.muted.set(!wasMuted);

    if (isRinging) {
      if (!wasMuted) {
        // Muting - stop ringtone
        this.stop();
      } else {
        // Unmuting - restart ringtone
        this.start();
      }
    }
  }

  resetMute(): void {
    this.muted.set(false);
  }

  private ensureAudioContext(): void {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }

    if (this.audioContext.state === 'suspended') {
      void this.audioContext.resume().catch(() => {
        // Resume may fail until user gesture.
      });
    }
  }

  private ensureRingtoneElement(): void {
    if (this.ringtoneAudio) {
      return;
    }

    const audio = new Audio(this.ringtoneSource);
    audio.preload = 'auto';
    audio.loop = true;
    this.ringtoneAudio = audio;
  }

  private playRingbackCycle(): void {
    if (!this.audioContext || this.ringbackOscillators.length > 0) {
      return;
    }

    const ctx = this.audioContext;
    const now = ctx.currentTime;
    const toneDurationSeconds = 1.5;

    const gain = ctx.createGain();
    const primary = ctx.createOscillator();
    const secondary = ctx.createOscillator();

    primary.type = 'sine';
    primary.frequency.value = 440;
    secondary.type = 'sine';
    secondary.frequency.value = 480;

    primary.connect(gain);
    secondary.connect(gain);
    gain.connect(this.ringbackDestination ?? ctx.destination);

    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.12, now + 0.02);
    gain.gain.setValueAtTime(0.12, now + toneDurationSeconds - 0.05);
    gain.gain.exponentialRampToValueAtTime(
      0.0001,
      now + toneDurationSeconds
    );

    primary.start(now);
    secondary.start(now);
    primary.stop(now + toneDurationSeconds);
    secondary.stop(now + toneDurationSeconds);

    this.ringbackGainNode = gain;
    this.ringbackOscillators = [primary, secondary];

    this.zone.runOutsideAngular(() => {
      this.ringbackStopTimeoutId = setTimeout(() => {
        this.stopActiveRingbackTone();
      }, (toneDurationSeconds + 0.1) * 1000);
    });
  }

  private stopActiveRingbackTone(): void {
    if (this.ringbackStopTimeoutId) {
      clearTimeout(this.ringbackStopTimeoutId);
      this.ringbackStopTimeoutId = null;
    }

    this.ringbackOscillators.forEach(osc => {
      try {
        osc.stop();
      } catch {
        // Oscillator might already be stopped.
      }
      osc.disconnect();
    });
    this.ringbackOscillators = [];

    if (this.ringbackGainNode) {
      this.ringbackGainNode.disconnect();
      this.ringbackGainNode = null;
    }
  }

  private ensureRingbackOutputRoute(): void {
    if (!this.audioContext) {
      return;
    }

    if (!this.ringbackDestination) {
      this.ringbackDestination = this.audioContext.createMediaStreamDestination();
    }

    if (!this.ringbackAudio) {
      const audio = new Audio();
      audio.preload = 'auto';
      audio.srcObject = this.ringbackDestination.stream;
      this.ringbackAudio = audio;
    } else if (!this.ringbackAudio.srcObject) {
      this.ringbackAudio.srcObject = this.ringbackDestination.stream;
    }

    this.deviceService.setAuxiliaryAudioElement(this.ringbackAudio);
    void this.ringbackAudio.play().catch(() => {
      // Playback can be blocked by browser autoplay policy.
    });
  }

  private pauseRingbackOutputRoute(): void {
    this.deviceService.setAuxiliaryAudioElement(null);

    if (!this.ringbackAudio) {
      return;
    }

    this.ringbackAudio.pause();
  }
}
